'use client'

import { useGSAPMedia, useMedia } from '@/hooks'
import { useKeyboardNav } from '@/hooks/use-keyboard-nav'
import { cn } from '@/lib/utils'
import { useStoreNavigation } from '@/store'
import { gsap, ScrollTrigger } from '@gsap'
import { MEDIA } from '@spot'
import { useLenis } from 'lenis/react'
import { usePathname } from 'next/navigation'
import { memo, useEffect, useEffectEvent, useRef } from 'react'
import BottomBar from './Components/BottomBar'
import Search from './Components/Search'
import TopBar from './Components/TopBar'
import HeaderProps from './type'

const Header = ({ data }: HeaderProps) => {
  const ref = useRef<HTMLElement>(null)
  const search = data?.header?.search

  const {
    setIsNavOpen,
    isSearchModalOpen,
    setIsNavReduced,
    isNavReduced,
    setIsSearchModalOpen,
    isNavOpen
  } = useStoreNavigation()

  const pathname = usePathname()
  const lenis = useLenis()
  const isDesktop = useMedia(MEDIA.desktop, true)

  const resetNavState = useEffectEvent(() => {
    document.body.dataset.navReduced = 'false'
    document.body.dataset.navFrozen = 'false'
    setIsNavReduced(false)
  })

  const handleIsDesktop = useEffectEvent(() => {
    if (isNavOpen) {
      setIsNavOpen(false)
    }
    if (isSearchModalOpen) {
      setIsSearchModalOpen(false)
    }
    if (isNavReduced) {
      setIsNavReduced(false)
    }
  })

  useGSAPMedia({
    scope: ref,
    media: MEDIA,
    callback(c) {
      if (isNavOpen) {
        return
      }
      gsap.registerPlugin(ScrollTrigger)
      ScrollTrigger.observe({
        type: 'wheel,touch,scroll',
        onUp: () => {
          if (!useStoreNavigation.getState().isNavReduced) {
            return
          }
          document.body.dataset.navReduced = 'false'
          setIsNavReduced(false)
        },
        onDown: () => {
          if (useStoreNavigation.getState().isNavReduced) {
            return
          }
          document.body.dataset.navReduced = 'true'
          setIsNavReduced(true)
        },
        tolerance: c?.conditions?.desktop ? 20 * 16 : 100
      })
    },
    deps: [isNavOpen]
  })

  useEffect(() => {
    resetNavState()
  }, [pathname])

  useEffect(() => {
    if (isNavOpen) {
      resetNavState()
      lenis?.stop()
    } else {
      lenis?.start()
    }
  }, [isNavOpen, lenis])

  useEffect(() => {
    handleIsDesktop()
  }, [isDesktop])

  useKeyboardNav({
    onEscape: () => {
      if (isNavOpen) {
        setIsNavOpen(false)
      }
      if (isSearchModalOpen) {
        setIsSearchModalOpen(false)
      }
      if (isNavReduced) {
        setIsNavReduced(false)
      }
    }
  })

  return (
    <header
      ref={ref}
      data-nav-open={isNavOpen}
      className={cn(
        'grid-template-rows-[auto_1fr] ease-out-cubic fixed top-0 left-0 z-1000000 grid w-full transition-transform duration-200 [body[data-nav-reduced=true]_&]:-translate-y-full'
      )}
    >
      <TopBar data={data?.header} />
      {search?.link?.href && <Search data={search} />}
      <BottomBar data={data} />
    </header>
  )
}

export default memo(Header)
